import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { duelService, duelActivityService } from '../../services/socialService';
import DuelHistoryCard from './components/DuelHistoryCard';
import HistoryFilters from './components/HistoryFilters';
import PerformanceStats from './components/PerformanceStats';
import PageTransition from '../../components/PageTransition';
import { useNavigate } from 'react-router-dom';



const DuelHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [duels, setDuels] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    result: 'all',
    sortBy: 'recent'
  });
  const [expandedDuelId, setExpandedDuelId] = useState(null);
  const [filteredDuels, setFilteredDuels] = useState([]);

  useEffect(() => {
    if (user?.id) {
      fetchDuelHistory();
      fetchUserStats();
    }
  }, [user?.id, filters]);

  const fetchDuelHistory = async () => {
    setLoading(true);
    try {
      const data = await duelService?.getUserDuels(user?.id);
      let filtered = data || [];

      // Apply filters
      if (filters?.status !== 'all') {
        filtered = filtered?.filter(d => d?.status === filters?.status);
      }

      if (filters?.result === 'won') {
        filtered = filtered?.filter(d => d?.winner_id === user?.id);
      } else if (filters?.result === 'lost') {
        filtered = filtered?.filter(d => d?.winner_id && d?.winner_id !== user?.id);
      }

      // Apply sorting
      if (filters?.sortBy === 'recent') {
        filtered?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else if (filters?.sortBy === 'oldest') {
        filtered?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      }

      setDuels(filtered);
    } catch (error) {
      console.error('Error fetching duel history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const stats = await duelActivityService?.getUserDuelStats(user?.id);
      setUserStats(stats);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...duels];

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(d => d?.status === filters?.status);
    }

    // Result filter
    if (filters?.result === 'won') {
      filtered = filtered?.filter(d => d?.winner_id === user?.id);
    } else if (filters?.result === 'lost') {
      filtered = filtered?.filter(d => d?.winner_id && d?.winner_id !== user?.id);
    }

    // Sort
    switch (filters?.sortBy) {
      case 'recent':
        filtered?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
        break;
      case 'oldest':
        filtered?.sort((a, b) => new Date(a?.created_at) - new Date(b?.created_at));
        break;
      default:
        break;
    }

    setFilteredDuels(filtered);
  }, [duels, filters, user?.id]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleToggleExpand = (duelId) => {
    setExpandedDuelId(prev => prev === duelId ? null : duelId);
  };

  return (
    <>
      <Helmet>
        <title>Duel History - PrimeOne+</title>
        <meta name="description" content="View your complete duel history with detailed records, opponent information, results, and performance statistics." />
      </Helmet>
      <Header />
      <PageTransition>
        <div className="min-h-screen bg-background">
          {/* Header Section */}
          <div className="bg-card border-b border-border/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <motion.div
                className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0"
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="flex items-center space-x-4">
                  <motion.div
                    className="flex items-center justify-center w-12 h-12 wellness-gradient-primary rounded-xl wellness-elevation-1"
                    whileHover={{ scale: 1.08, rotate: -5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <Icon name="History" size={24} color="white" />
                  </motion.div>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">Historial de Duelos</h1>
                    <p className="text-muted-foreground mt-1">
                      Revisa tus competencias pasadas y estadísticas de rendimiento
                    </p>
                  </div>
                </div>
                
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="outline"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={() => window.location?.reload()}
                  >
                    Actualizar
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <motion.div
                  className="rounded-full h-12 w-12 border-b-2 border-primary"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              </div>
            ) : (
              <motion.div
                className="space-y-8"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Performance Statistics */}
                <PerformanceStats
                  wins={userStats?.duels_won || 0}
                  losses={userStats?.duels_lost || 0}
                  draws={userStats?.duels_completed || 0}
                  winRate={userStats?.duels_completed > 0 
                    ? Math.round((userStats?.duels_won / userStats?.duels_completed) * 100)
                    : 0
                  }
                  totalDuels={userStats?.duels_completed || 0}
                  activeDuels={userStats?.duels_completed || 0}
                  duels={duels}
                  userId={user?.id}
                />

                {/* Filters */}
                <HistoryFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  totalCount={filteredDuels?.length}
                  friends={[]}
                />

                {/* Duel Records */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-foreground">
                      Registros de Duelos ({filteredDuels?.length})
                    </h2>
                  </div>

                  {filteredDuels?.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-12 bg-card rounded-xl border-2 border-dashed border-border text-center"
                    >
                      <Icon name="Swords" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No se encontraron duelos
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        {filters?.status !== 'all' || filters?.opponent !== 'all' || filters?.metric !== 'all' || filters?.dateRange !== 'all' ? 'Intenta ajustar los filtros para ver más resultados' : 'Aún no has participado en ningún duelo. ¡Comienza tu primer desafío!'}
                      </p>
                    </motion.div>
                  ) : (
                    <div className="space-y-4">
                      <AnimatePresence>
                        {filteredDuels?.map((duel, index) => (
                          <DuelHistoryCard
                            key={duel?.id}
                            duel={duel}
                            userId={user?.id}
                            index={index}
                            isExpanded={expandedDuelId === duel?.id}
                            onToggleExpand={() => handleToggleExpand(duel?.id)}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default DuelHistory;